// =============================================================================
// ui/state/useAiVerdict.ts — one model call per account+period, and no more.
// -----------------------------------------------------------------------------
// ── 🚨 THIS HOOK EXISTS TO STOP A RENDER LOOP BECOMING A BILL ──
//
// Every ask costs money and burns one of a capped daily allowance. A component
// that re-renders on a filter change, a preferences load, or a parent's state
// update would otherwise fire again each time — and the trader would be locked
// out of their own coaching for the rest of the day by a bug they never saw.
//
// Three things prevent it, and all three are load-bearing:
//
//   1. A MODULE-LEVEL cache keyed by account|period|window. It outlives the
//      component, because `AppShell` remounts the whole tab subtree on
//      navigation (D94) — a cache held in state would be discarded by the act of
//      leaving the tab and rebought on return.
//   2. An in-flight set on the same key, so two renders inside one round trip
//      cannot stack two calls.
//   3. The gate is checked BEFORE the request, never after.
//
// ⚠ THE CACHE IS DELIBERATELY NOT PERSISTED. A verdict is about a window of
// trading that keeps moving, and a stale one restored from disk would read as
// current. Regenerate clears its own key.
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSupabase } from './supabase';
import { AiCoachError, fetchAiVerdict, type AiVerdict } from '../../io/ai/aiCoach';
import { reportFailure } from '../../io/diagnostics/errorLog';

export type AiPhase = 'idle' | 'loading' | 'ready' | 'failed';

export interface AiVerdictState {
  phase: AiPhase;
  verdict: AiVerdict | null;
  error: string | null;
  /** Set when the failure is a normal state rather than a fault. */
  code: string | null;
  regenerate: () => void;
}

/** Survives the tab remount. See the header. */
const CACHE = new Map<string, AiVerdict>();
const IN_FLIGHT = new Set<string>();

/** Test seam — the suite must never inherit another test's cached verdict. */
export function __resetAiVerdictCache(): void {
  CACHE.clear();
  IN_FLIGHT.clear();
}

export interface AiVerdictInput {
  /** Null when the gate is closed — no call is made and no key is cached. */
  key: string | null;
  payload: Record<string, unknown> | null;
}

export function useAiVerdict({ key, payload }: AiVerdictInput): AiVerdictState {
  const { sb } = useSupabase();
  const [phase, setPhase] = useState<AiPhase>('idle');
  const [verdict, setVerdict] = useState<AiVerdict | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // ⚠ A REF, not a dependency. The payload object is rebuilt on every render, so
  // depending on it would refire the effect — and the whole point of the key is
  // that the payload only matters when the key changes.
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  const regenerate = useCallback(() => {
    if (key) CACHE.delete(key);
    setNonce((n) => n + 1);
  }, [key]);

  useEffect(() => {
    if (!key || !payloadRef.current) {
      setPhase('idle');
      setVerdict(null);
      setError(null);
      setCode(null);
      return;
    }

    const cached = CACHE.get(key);
    if (cached) {
      setVerdict(cached);
      setPhase('ready');
      setError(null);
      setCode(null);
      return;
    }

    // ⚠ Another mount of this same key is already paying for this call. Wait for
    // it rather than buying a second copy; the cache will hold the answer.
    if (IN_FLIGHT.has(key)) {
      setPhase('loading');
      return;
    }

    let cancelled = false;
    IN_FLIGHT.add(key);
    setPhase('loading');
    setError(null);
    setCode(null);

    void (async () => {
      try {
        const out = await fetchAiVerdict(sb, payloadRef.current!, key);
        CACHE.set(key, out);
        if (cancelled) return;
        setVerdict(out);
        setPhase('ready');
      } catch (e) {
        if (cancelled) return;
        // ⚠ BELT AND BRACES ON THE STATE, and it is worth being precise about
        // that: the CARD already branches on `phase === 'failed'` before it
        // looks at `verdict`, so a stale one cannot reach the screen today. A
        // sabotage removing this line stays green for exactly that reason.
        //
        // It is kept because the hook's contract should be true on its own —
        // "failed" must not also mean "and here is the previous account's
        // read". A future consumer that renders `verdict` whenever it is present
        // would otherwise show one account's drawdown under another's name.
        setVerdict(null);
        setError(reportFailure('AI verdict', e, 'The coach could not be reached.'));
        setCode(e instanceof AiCoachError ? e.code : 'AI_FAILED');
        setPhase('failed');
      } finally {
        IN_FLIGHT.delete(key);
      }
    })();

    return () => { cancelled = true; };
  }, [sb, key, nonce]);

  return useMemo(
    () => ({ phase, verdict, error, code, regenerate }),
    [phase, verdict, error, code, regenerate],
  );
}
